import Hyperlink from '@ingka/hyperlink';
import Button from '@ingka/button';
import SSRIcon from '@ingka/ssr-icon';
import Tooltip from '@ingka/tooltip';
import Select, { Option } from '@ingka/select';
import arrowLeft from '@ingka/ssr-icon/paths/arrow-left';
import arrowRight from '@ingka/ssr-icon/paths/arrow-right';
import informationCircle from '@ingka/ssr-icon/paths/information-circle';
import Table, { TableBody, TableHeader } from '@ingka/table';
import Text from '@ingka/text';
import Loading, { LoadingBall } from '@ingka/loading';
import Checkbox from '@ingka/checkbox';
import { DateTime } from 'luxon';
import { chunk } from 'lodash';
import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocale } from '@lotta/self-service/lottery-fe/feature-hooks';
import type { LotteryDraw, Winner, WinnerEvents } from '@lotta/shared/types-lottery-schemas';
import {
  useAddWinnersMutation,
  useInvalidateWinnersMutation,
  useReplaceWinnersMutation,
} from '@lotta/self-service/lottery-fe/data-access-state';
import { ConfigContext } from '@lotta/self-service/lottery-fe/util-config';
import styles from './WinnersTable.module.scss';
import { AddWinnersModal } from './components/AddWinnersModal';
import { InvalidateWinnersModal } from './components/InvalidateWinnersModal';

type EventTypes = keyof WinnerEvents;

/**
 * Get last event, but prioritize PRIZE_CLAIMED
 */
export const getLatestEvent = (events: WinnerEvents): EventTypes | undefined => {
  if (events.WINNER_INVALIDATED) {
    return 'WINNER_INVALIDATED';
  }
  if (events.PRIZE_CLAIMED) {
    return 'PRIZE_CLAIMED';
  }
  const eventEntries = Object.entries(events);
  if (eventEntries.length <= 0) {
    return undefined;
  }
  eventEntries.sort(
    (event, otherEvent) =>
      DateTime.fromISO(otherEvent[1].date).toMillis() - DateTime.fromISO(event[1].date).toMillis()
  );
  return eventEntries[0][0] as EventTypes;
};

export const eventTranslationKeys: Record<EventTypes, string> = {
  WINNER_CREATED: 'winner-event-created',
  WINNER_INVALIDATED: 'winner-event-invalidated',
  PRIZE_CLAIMED: 'winner-event-prize-claimed',
  EMAIL_EVENT_SENT: 'winner-event-email-sent',
  EMAIL_EVENT_DELIVERY: 'winner-event-email-delivery',
  EMAIL_EVENT_INITIAL_OPEN: 'winner-event-email-initial-open',
  EMAIL_EVENT_OPEN: 'winner-event-email-open',
  EMAIL_EVENT_CLICK: 'winner-event-email-click',
  EMAIL_EVENT_BOUNCE: 'winner-event-email-bounce',
  EMAIL_EVENT_COMPLAINT: 'winner-event-email-complaint',
};

export type WinnersTableProps = {
  winners: Winner[];
  lotteryId: string;
  lotteryDraw: LotteryDraw;
  refetchWinners: () => void;
};

export const WinnersTable: React.FC<WinnersTableProps> = ({
  winners,
  lotteryId,
  lotteryDraw,
  refetchWinners,
}) => {
  const { retailUnit } = useLocale();
  const { t } = useTranslation();
  const { customerAdminDomain } = useContext(ConfigContext);
  const [winnerPage, setWinnerPage] = useState(0);
  const [selectedWinners, setSelectedWinners] = useState<Set<string>>(new Set());
  const [addWinnersModalVisible, setAddWinnersModalVisible] = useState(false);
  const [invalidateWinnersModalVisible, setInvalidateWinnersModalVisible] = useState(false);
  const [replaceWinnersModalVisible, setReplaceWinnersModalVisible] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);
  const pageSize = 50;
  const [invalidateWinners] = useInvalidateWinnersMutation();
  const [replaceWinners] = useReplaceWinnersMutation();
  const [addWinners] = useAddWinnersMutation();
  const winnerPages = chunk(winners, pageSize);

  const handleRefetch = () => {
    setIsRefetching(true);
    setTimeout(() => {
      refetchWinners();
      setIsRefetching(false);
    }, 15000);
  };

  const disableHandleWinners =
    DateTime.fromISO(lotteryDraw.endDate).plus({ days: 60 }) < DateTime.now();

  return (
    <>
      <div className={styles['table-header']}>
        <div className={styles['text-div']}>
          <SSRIcon paths={informationCircle} color="#0058A3" />
          <Text bodySize="s" className={styles['information-text']}>
            {t('draw-details-uid-disclaimer')}
          </Text>
        </div>
        <div className={styles['button-div']}>
          <Button
            small
            disabled={disableHandleWinners || !(selectedWinners.size > 0)}
            onClick={() => setInvalidateWinnersModalVisible(true)}
          >
            {t('invalidate-winners-modal-title')}
          </Button>
          <InvalidateWinnersModal
            visible={invalidateWinnersModalVisible}
            partyUids={selectedWinners}
            replace={false}
            onClose={() => setInvalidateWinnersModalVisible(false)}
            onConfirm={() => {
              invalidateWinners({
                retailUnit: retailUnit.toUpperCase(),
                lotteryId,
                lotteryDrawId: lotteryDraw.id,
                winners: Array.from(selectedWinners).map((winner) => ({
                  partyUid: winner,
                })),
              });
              setSelectedWinners(new Set());
              setInvalidateWinnersModalVisible(false);
              handleRefetch();
            }}
          />
          <Button
            small
            disabled={disableHandleWinners || selectedWinners.size === 0}
            onClick={() => setReplaceWinnersModalVisible(true)}
          >
            {t('invalidate-winners-modal-replace-title')}
          </Button>
          <InvalidateWinnersModal
            visible={replaceWinnersModalVisible}
            partyUids={selectedWinners}
            replace={true}
            onClose={() => setReplaceWinnersModalVisible(false)}
            onConfirm={() => {
              replaceWinners({
                retailUnit: retailUnit.toUpperCase(),
                lotteryId,
                lotteryDrawId: lotteryDraw.id,
                winners: Array.from(selectedWinners).map((winner) => ({
                  partyUid: winner,
                })),
              });
              setSelectedWinners(new Set());
              setReplaceWinnersModalVisible(false);
              handleRefetch();
            }}
          />
          <Button
            small
            disabled={disableHandleWinners}
            onClick={() => setAddWinnersModalVisible(true)}
          >
            {t('add-winners-modal-title')}
          </Button>
          <AddWinnersModal
            visible={addWinnersModalVisible}
            onClose={() => setAddWinnersModalVisible(false)}
            onConfirm={(count) => {
              addWinners({
                retailUnit: retailUnit.toUpperCase(),
                lotteryId,
                lotteryDrawId: lotteryDraw.id,
                numberOfNewWinners: count,
              });
              setSelectedWinners(new Set());
              setAddWinnersModalVisible(false);
              handleRefetch();
            }}
          />
          {disableHandleWinners && <Tooltip tooltipText={t('handle-winners-tooltip-disabled')} />}
        </div>
      </div>
      {isRefetching ? (
        <div className={styles['loading-div']}>
          <Loading text={t('winner-refetch-text')}>
            <LoadingBall />
          </Loading>
        </div>
      ) : (
        <Table inset>
          <TableHeader sticky>
            <tr>
              <th>{t('general-name')}</th>
              <th>{t('general-partyuid')}</th>
              <th>{t('draw-details-header-event-status')}</th>
              <th>{t('general-select')}</th>
            </tr>
          </TableHeader>

          <TableBody striped className={styles['body']} data-testid="winners-table-body">
            {winnerPages[winnerPage] &&
              winnerPages[winnerPage].map(({ partyUid, events, updatedAt }, index) => {
                const winnerId = index + winnerPage * pageSize + 1;
                const catLink = `https://${customerAdminDomain}/${retailUnit.toUpperCase()}/private/${partyUid}/start`;
                const latestEvent = getLatestEvent(events);

                const isWithin10Minutes =
                  Date.now() - new Date(updatedAt).getTime() <= 10 * 60 * 1000;

                return (
                  <tr
                    key={partyUid}
                    className={
                      latestEvent === 'WINNER_INVALIDATED'
                        ? styles['invalid']
                        : isWithin10Minutes
                        ? styles['new']
                        : ''
                    }
                  >
                    <td className={styles['heading']}>
                      {t('draw-details-table-winner', { winnerId })}
                    </td>
                    <td>
                      <Hyperlink url={catLink} text={partyUid} newWindow />
                    </td>

                    <td>
                      {latestEvent !== undefined
                        ? t(eventTranslationKeys[latestEvent])
                        : t('winner-event-no-events')}
                    </td>
                    <td className={styles['checkbox']}>
                      <Checkbox
                        id={partyUid}
                        value={partyUid}
                        disabled={latestEvent === 'WINNER_INVALIDATED'}
                        onChange={(e) => {
                          setSelectedWinners((prev) => {
                            const updated = new Set(prev);
                            if (e.target.checked) {
                              updated.add(partyUid);
                            } else {
                              updated.delete(partyUid);
                            }
                            return updated;
                          });
                        }}
                      />
                    </td>
                  </tr>
                );
              })}
          </TableBody>
        </Table>
      )}
      {winnerPages.length > 1 && (
        <div className={styles['button-div']} data-testid="pagination">
          <Button
            small
            iconOnly
            ssrIcon={arrowLeft}
            onClick={() => setWinnerPage(winnerPage - 1)}
            disabled={winnerPage === 0}
          />
          <Select
            hintText={''}
            id={'winner-page-select'}
            value={`${winnerPage}`}
            onChange={(e) => setWinnerPage(Number(e.target.value))}
          >
            {winnerPages.map((_, index) => (
              <Option
                // eslint-disable-next-line react/no-array-index-key
                key={`page-number-${index}`}
                name={`${index * pageSize + 1}-${Math.min((index + 1) * pageSize, winners.length)}`}
                value={`${index}`}
              />
            ))}
          </Select>
          <Button
            small
            iconOnly
            disabled={winnerPage === winnerPages.length - 1}
            ssrIcon={arrowRight}
            onClick={() => setWinnerPage(winnerPage + 1)}
          />
        </div>
      )}
    </>
  );
};
